<template>
  <div class="sidebar lg:my-0 my-8 sticky top-16">
    <h2 v-if="tocItems.length > 0" class="px-4 py-3 toc-header">On This Page</h2>
    <ul class="toc-list">
      <li
        v-for="item in tocItems"
        :key="item.id"
        class="toc-item"
        :class="{ 'active-toc-item': selectedItem && selectedItem.id === item.id }"
        :style="getItemPadding(item.level)"
      >
        <a
          :href="'#' + item.id"
          @click.prevent="scrollToHeading(item)"
          class="toc-link"
          :class="{ active: selectedItem && selectedItem.id === item.id }"
        >
          {{ item.text }}
        </a>
      </li>
    </ul>
  </div>
</template>

<script>
const INDENT_PIXELS_PER_LEVEL = 0;

export default {
  data() {
    return {
      tocItems: [],
      selectedItemId: null,
      headingObserver: null,
    };
  },
  computed: {
    selectedItem() {
      return this.tocItems.find((item) => item.id === this.selectedItemId) || null;
    },
  },
  mounted() {
    this.generateToc();

    const contentContainer = document.querySelector('.location-content');
    if (contentContainer) {
      const observer = new MutationObserver(() => this.generateToc());
      observer.observe(contentContainer, {
        childList: true,
        subtree: true,
      });
      this.mutationObserver = observer;
    }

    this.setupIntersectionObserver();

    this.handleEmptyToc();
  },
  beforeUnmount() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
    this.disconnectIntersectionObserver();
  },
  methods: {
    handleEmptyToc() {
      if (this.tocItems.length > 0) {
        return;
      }
      document.querySelector('.toc-container').classList = 'col-span-2';
      document.querySelector('.location-content ').classList = 'article-content location-content text-left lg:text-justify col-span-1 lg:col-span-8'
    },
    getItemPadding(level) {
      const padding = (level - 1) * INDENT_PIXELS_PER_LEVEL;
      return { 'padding-left': `${padding}px` };
    },

    scrollToHeading(item) {
      this.selectedItemId = item.id;
      document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' });
    },

    generateToc() {
      this.disconnectIntersectionObserver();

      const content = document.querySelector('.location-content');
      if (!content) {
        this.tocItems = [];
        return;
      }

      const headings = content.querySelectorAll('h1, h2, h3, h4, h5, h6');
      this.tocItems = Array.from(headings).map((heading) => {
        // Ensure every heading has a unique ID
        if (!heading.id) {
          heading.id = this.slugify(heading.textContent);
        }
        return {
          text: heading.textContent,
          id: heading.id,
          level: parseInt(heading.tagName.charAt(1)),
        };
      });

      this.setupIntersectionObserver();
    },

    setupIntersectionObserver() {
      const self = this;

      this.headingObserver = new IntersectionObserver(
        (entries) => {
          const intersectingEntry = entries.find((entry) => entry.isIntersecting);

          if (intersectingEntry) {
            const activeItem = self.tocItems.find(
              (item) => item.id === intersectingEntry.target.id
            );
            if (activeItem) {
              self.selectedItemId = activeItem.id;
            }
          }
        },
        { threshold: 0.1, rootMargin: '-10% 0px -85% 0px' }
      );

      this.tocItems.forEach((item) => {
        const heading = document.getElementById(item.id);
        if (heading) this.headingObserver.observe(heading);
      });
    },

    disconnectIntersectionObserver() {
      if (this.headingObserver) {
        this.headingObserver.disconnect();
        this.headingObserver = null;
      }
    },

    slugify(text) {
      return text
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_-]+/g, '-')
        .replace(/^-+|-+$/g, '');
    },
  },
};
</script>

<style lang="scss">
.toc-header {
  color: rgba(255, 255, 255, 0.7);
  font-family: Merriweather;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}
.sidebar {
  border-radius: 2px;
  // max-width: 210px;
  background: #2c2c2c;
}

.toc-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.toc-item {
  display: flex;
  align-items: center;
  // align-self: stretch;

  padding-left: 16px;
}

.toc-link {
  display: block;
  width: 100%;
  padding: 8px 16px;
  text-decoration: none;

  color: #fff;
  font-family: Merriweather;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  text-transform: uppercase;
  transition: background-color 0.2s;
}

.toc-link:hover {
  background: rgba(255, 255, 255, 0.12);
}

.active-toc-item {
  border-left: 4px solid #ffd647;

  & .toc-link {
    background: rgba(255, 255, 255, 0.12);
  }
}

.lg-backdrop.in {
  background: rgba(0, 0, 0, 0.85);
}

.sidebar {
    position: fixed;
    top: 64px;
}
</style>
